/**
 * The seven set-up steps of a new integration, and what each needs before a
 * person can go on (doc 07, screen 2).
 */

import type { ImportFieldInfo, Preview, ScoutJob } from "@/lib/integrations/api";
import { filledCount, isMapped, type Settings } from "@/lib/integrations/settings";

export const WIZARD_STEPS = [
  "Source",
  "Where it goes",
  "Rows",
  "Fields",
  "Match & safety",
  "Try",
  "Switch on",
] as const;

export type WizardState = {
  job: Pick<ScoutJob, "id" | "lastPassed"> | null;
  event: string;
  command: string;
  code: string;
  name: string;
  /** Set once the integration exists on the server. */
  created: boolean;
  settings: Settings;
  fields: ImportFieldInfo[];
  preview: Pick<Preview, "counts" | "rows" | "missing"> | null;
  tried: boolean;
};

const CODE = /^[a-z0-9][a-z0-9-]{1,63}$/;

export function codeFromName(name: string): string {
  return name
    .toLowerCase()
    .normalize("NFKD")
    .replace(/[̀-ͯ]/g, "")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 64);
}

export function codeProblem(code: string): string | null {
  if (!code) return "Give it a code.";
  if (!CODE.test(code)) return "The code needs 2 to 64 small letters, numbers or dashes, and cannot start with a dash.";
  return null;
}

/** Required fields with nothing mapped, or with no value in any preview row. */
export function requiredGaps(state: Pick<WizardState, "fields" | "settings" | "preview">): {
  unmapped: string[];
  empty: string[];
} {
  const needed = state.fields.filter((field) => field.required && !field.many);
  const unmapped = needed.filter((field) => !isMapped(state.settings.fields[field.key])).map((field) => field.label);
  const kept = (state.preview?.rows ?? []).filter((row) => row.outcome !== "skipped").map((row) => row.value);
  const empty =
    kept.length === 0
      ? []
      : needed
          .filter((field) => isMapped(state.settings.fields[field.key]) && filledCount(kept, field.key) === 0)
          .map((field) => field.label);
  return { unmapped, empty };
}

function list(words: string[]): string {
  if (words.length <= 1) return words.join("");
  return `${words.slice(0, -1).join(", ")} and ${words[words.length - 1]}`;
}

/** Why a person cannot leave step `index` yet, or null when they can. */
export function stepProblem(index: number, state: WizardState): string | null {
  switch (index) {
    case 0:
      if (!state.job) return "Pick a Scout job, or build a new one.";
      if (!state.job.lastPassed) return "This job has no passed run to learn from.";
      return null;
    case 1:
      if (!state.event || !state.command) return "Pick the event, the workflow and its import command.";
      if (!state.name.trim()) return "Give it a name.";
      return codeProblem(state.code);
    case 2: {
      if (!state.preview) return "Wait for the preview of the rows.";
      const counts = state.preview.counts;
      const total = Object.values(counts).reduce((sum, count) => sum + count, 0);
      if (total === 0) return "This run has no rows. Pick another run or check the job.";
      if ((counts.skipped ?? 0) >= total) return "The keep rules leave out every row. Keep at least one row.";
      return null;
    }
    case 3: {
      const gaps = requiredGaps(state);
      if (gaps.unmapped.length) return `These fields need a value first: ${list(gaps.unmapped)}.`;
      if (gaps.empty.length) return `These fields are empty in every row: ${list(gaps.empty)}.`;
      return null;
    }
    case 5:
      return state.tried ? null : "Try the draft on a real run first.";
    default:
      return null;
  }
}

/** The furthest step a person may open: every step before it is done. */
export function furthestStep(state: WizardState): number {
  for (let index = 0; index < WIZARD_STEPS.length - 1; index += 1) {
    if (stepProblem(index, state) !== null) return index;
  }
  return WIZARD_STEPS.length - 1;
}
